import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { ContentStatus, StudioContentsTabType } from '@diritto/common/content/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { convertToDotString } from '@diritto/common/utils/date';
import { numberComma } from '@diritto/common/utils/string';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import {
  ClickAction,
  ClickTargetType,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { NotImplementedError } from '@diritto/common/base/exceptions';
import { UserMeDto } from '@diritto/common/account/dtos/user-me.dto';
import { UserType } from '@diritto/common/account/constants/user.constant';
import { ProposalWantedBadgeIcon } from 'icons/badge/ProposalWantedBadgeIcon';
import { useTableHook } from 'app-components/studio/myContents/useTableHook';
import { EmptyList } from 'ui-components/common/EmptyList';
import { Button } from 'ui-components/common/Button';
import { ContentCover } from 'ui-components/contents/ContentCover';
import {
  PinnedTh,
  Table,
  TableContainer,
  TableWrapper,
  Tbody,
  Td,
  Th,
  Thead,
  ThInTBody,
  Tr,
} from 'app-components/studio/myContents/table';
import { Link } from 'ui-components/common/Link';
import { ArrowHorizontalIcon } from 'icons/ArrowHorizontalIcon';
import { MoveIcon } from 'icons/MoveIcon';
import { getTextLineStyle } from 'styles/common';
import { MyContentMoreMenu } from 'app-components/studio/myContents/contents/MyContentMoreMenu';
import { ButtonSize } from 'constants/button';
import { isPC } from 'constants/layout';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ZIndex } from 'constants/zIndex';
import { LockIcon } from 'icons/LockIcon';

export const MyContentList = ({
  tabType,
  contents,
  user,
  onAddContent,
}: {
  tabType: StudioContentsTabType;
  contents: ContentPublicRelayDto[];
  user?: UserMeDto;
  onAddContent: () => void;
}) => {
  const { tableContainerRef, showBoxShadow } = useTableHook();
  const { tracker } = useTrackingState();

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType) =>
      tracker.track(TrackingEventType.Click, {
        action,
        clickType,
        trackerExtraInfo: getTrackingPosition(tabType),
      }),
    [tabType, tracker],
  );

  return (
    <TableWrapper>
      <TableContainer ref={tableContainerRef}>
        <Table>
          <Thead showShadow={showBoxShadow.horizontal}>
            <tr>
              <StyledPinnedTh showShadow={showBoxShadow.vertical}>작품</StyledPinnedTh>
              <Th minWidth={80}>회차 수</Th>
              <Th minWidth={80}>업데이트</Th>
              <Th minWidth={80}>연재 시작일</Th>
              <Th minWidth={80}>조회</Th>
              <Th minWidth={80}>댓글</Th>
              <Th minWidth={80}>추천</Th>
              <Th minWidth={80}>작품 좋아요</Th>
              <Th minWidth={80}>알림</Th>
              <Th minWidth={80}>이멋공</Th>
              <Th minWidth={80}>작품 관리</Th>
              <Th minWidth={80}>게시된 작품</Th>
              <Th minWidth={80}>옵션</Th>
            </tr>
          </Thead>
          <Tbody>
            {contents.map(content => (
              <Tr key={content.id}>
                <ThInTBody isPinned={showBoxShadow.vertical}>
                  <Link
                    href={`/studio/contents/${content.id}`}
                    onClick={() => handleTrack(ClickAction.ToDirittoStudioContentEpisodes, ClickTargetType.Title)}>
                    <StyledContentCoverMetaWrapper>
                      <StyledNewContentCover content={content} size={80} pcSize={90} />
                      <StyledContentMetaWrapper>
                        <StyledTitleWrapper>
                          {content.isProposalWanted && <ProposalWantedBadgeIcon />}
                          <StyledContentTitle>
                            {content.status === ContentStatus.Private ? (
                              <StyledLockIcon className="size-[14px]" />
                            ) : null}
                            {content.title}
                          </StyledContentTitle>
                        </StyledTitleWrapper>
                        <StyledContentDesc>{content.description}</StyledContentDesc>
                      </StyledContentMetaWrapper>
                    </StyledContentCoverMetaWrapper>
                  </Link>
                </ThInTBody>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.episodeCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {content.latestEpisodeOpenedAt
                      ? convertToDotString(content.latestEpisodeOpenedAt, true, true)
                      : '-'}
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {content.firstOpenedAt ? convertToDotString(content.firstOpenedAt, true, true) : '-'}
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.viewCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.replyCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.recommendCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.favoriteCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.newEpisodeNotiCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>{numberComma(content.counts.fancyShareCount)}</StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    <Link
                      href={`/studio/contents/${content.id}`}
                      onClick={() => handleTrack(ClickAction.ToDirittoStudioContentEpisodes, ClickTargetType.Icon)}>
                      <ArrowHorizontalIcon direction="right" color={Color.Black} size={20} />
                    </Link>
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    {content.status === ContentStatus.Private ? (
                      <MoveIcon color={Color.Black} size={20} opacity={0.3} />
                    ) : (
                      <Link href={`/contents/${content.id}`}>
                        <MoveIcon color={Color.Black} size={20} />
                      </Link>
                    )}
                  </StyledTdWrapper>
                </Td>
                <Td>
                  <StyledTdWrapper>
                    <MyContentMoreMenu content={content} />
                  </StyledTdWrapper>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      {user?.type === UserType.Partner && <PartnerRestricted />}
      {user?.type !== UserType.Partner && contents.length === 0 && (
        <Empty
          tabType={tabType}
          onAddContent={() => {
            handleTrack(ClickAction.ToDirittoStudioContentAdd, ClickTargetType.EmptyStateButton);
            onAddContent();
          }}
        />
      )}
    </TableWrapper>
  );
};

const getTrackingPosition = (tabType: StudioContentsTabType) => {
  switch (tabType) {
    case StudioContentsTabType.Serialized: {
      return { trackerPosition: TrackerPosition.DirittoStudioContentSerializedList };
    }
    case StudioContentsTabType.CompletedOrPublished: {
      return { trackerPosition: TrackerPosition.DirittoStudioContentCompletedOrPublishedList };
    }
    case StudioContentsTabType.All: {
      return { trackerPosition: TrackerPosition.DirittoStudioContentAllList };
    }
    default:
      throw new NotImplementedError();
  }
};

const Empty = ({ tabType, onAddContent }: { tabType: StudioContentsTabType; onAddContent: () => void }) => {
  if (tabType === StudioContentsTabType.CompletedOrPublished) {
    return <StyledEmptyList title="완결/출간 작품이 없어요." description="작품 상태가 변경되면 여기에 나타나요." />;
  }

  return (
    <StyledEmptyList title="작품이 없어요." description="연재를 시작해보세요!">
      <Button
        key="add"
        buttonTypography={Typography.XXXSmall}
        size={ButtonSize.Small}
        label="새 작품 등록하기"
        onClick={onAddContent}
      />
    </StyledEmptyList>
  );
};

const PartnerRestricted = () => (
  <StyledEmptyList title="파트너 계정으로는 스튜디오를 사용할 수 없어요." description="다른 계정을 이용해 주세요." />
);

const StyledTdWrapper = styled.div`
  width: 100%;
  height: 167px;
  padding: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-grow: 0;
  white-space: pre-line;

  ${Typography.XXSmall};
  font-weight: ${FontWeight.Regular};
`;

const StyledContentCoverMetaWrapper = styled.div`
  width: 100%;
  height: 167px;
  padding: 0 16px;
  display: flex;
  gap: 16px;
  align-items: center;
`;

const StyledNewContentCover = styled(ContentCover)`
  flex-grow: 0;
  flex-shrink: 0;
`;

const StyledContentMetaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  flex-grow: 0;
  max-height: 135px;
`;

const StyledTitleWrapper = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
`;

const StyledContentTitle = styled.div`
  text-align: left;

  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};

  ${getTextLineStyle(2)};
`;

const StyledContentDesc = styled.div`
  text-align: left;

  color: ${Color.Gray600};

  ${Typography.XXXSmall};
  font-weight: ${FontWeight.Medium};

  ${getTextLineStyle(3)};
`;

const StyledEmptyList = styled(EmptyList)`
  margin-top: 150px;
  position: relative;
  z-index: ${ZIndex.DirittoStudioEmptyList};
`;

const StyledPinnedTh = styled(PinnedTh)`
  width: 236px;

  @media (${isPC}) {
    width: 298px;
  }
`;

const StyledLockIcon = styled(LockIcon)`
  display: inline-block;
  vertical-align: middle;
  margin-right: 4px;
`;
